import * as Sentry from "@sentry/react";
import React from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { produce } from "immer";
import { debounce } from "lodash";
import { getIn, makeId } from "../Utils";
import _ from "lodash";
import { PROSE_MODES } from "../components/StoryBible";
import { countWords } from "../Utils";
import Auth from "src/Auth";
import Project from "../models/Project";
import Subscription from "src/models/Subscription";
import { createSafeStorage } from "./safeStorage";
import { CrossAppSignal, useCrossAppSignalStore } from "./crossAppSignalStore";
import { useCommentStore } from "./commentStore";

/** In-flight coalescing: per-projectId pending promise for refreshCharacters */
const _inflightCharacters = new Map();
const _charactersRefreshVersions = new Map();
// Never reuse a version after cleanup while older requests may still be pending.
let _entityRefreshSequence = 0;
/** In-flight coalescing: per-projectId pending promise for refreshWorldbuildingEntries */
const _inflightWorldbuilding = new Map();
const _worldbuildingRefreshVersions = new Map();

// Story Bible lists are critical project-bootstrap data. A browser can
// occasionally receive the response but fail while reading its body, so opt
// these two endpoints into Auth's narrowly scoped, exponential retry path.
const STORY_BIBLE_RESPONSE_READ_FAILURE_RETRIES = 2;
const STORY_BIBLE_RESPONSE_READ_FAILURE_RETRY_DELAY_MS = 500;

function setProjectFlag(projectFlags, projectId, enabled) {
  const nextProjectFlags = { ...(projectFlags || {}) };
  if (enabled) {
    nextProjectFlags[projectId] = true;
  } else {
    delete nextProjectFlags[projectId];
  }
  return nextProjectFlags;
}

function hasProjectFlags(projectFlags) {
  return Object.keys(projectFlags || {}).length > 0;
}

/** Skip duplicate Feedback run triggers within one response (picker auto-start then same `run_manuscript_feedback` tool). */
const AGENTIC_FEEDBACK_RUN_DEDUPE_MS = 2000;
/** After picker submit, ignore `run_manuscript_feedback` from the assistant for this long (it replaces the client's full-type run). */
const AGENTIC_SUPPRESS_FEEDBACK_OVERRIDE_MS = 12_000;
let agenticFeedbackRunDedupe = { sig: "", t: 0 };
/** Monotonic suppress-until timestamp for tool-triggered overrides (ms since epoch). */
let suppressAgenticFeedbackToolOverrideUntilMs = 0;

function reportStaleStoryBibleEntityRefresh(entityType, projectId, refreshVersion, currentVersion, force, source) {
  Sentry.addBreadcrumb({
    category: "story_bible_entity_refresh",
    level: "info",
    message: "Ignored stale Story Bible entity refresh",
    data: {
      entityType,
      projectId,
      refreshVersion,
      currentVersion,
      force: String(force),
      source,
    },
  });
}

function reportStoryBibleEntityRefreshFailure(error, entityType, projectId, refreshVersion, currentVersion, force, source) {
  Sentry.captureException(error, {
    tags: {
      feature: "story_bible_entity_refresh",
      entityType,
      force: String(force),
      stale: String(currentVersion !== refreshVersion),
    },
    extra: {
      projectId,
      refreshVersion,
      currentVersion,
      source,
    },
  });
}

function canonicalAgenticFeedbackRequest(feedbackTypeNames) {
  if (feedbackTypeNames == null) {
    return { sig: "all", normalized: null };
  }
  const normalized = [...feedbackTypeNames].map((n) => String(n).trim()).filter(Boolean);
  return {
    sig: normalized.length === 0 ? "all_explicit" : `names:${normalized.join("\u001f")}`,
    normalized,
  };
}

function flattenEntityFields(fields) {
  if (!fields || typeof fields !== "object") return fields;
  const result = { ...fields };
  if (result.customFields && typeof result.customFields === "object" && !Array.isArray(result.customFields)) {
    for (const [key, value] of Object.entries(result.customFields)) {
      result[`custom.${key}`] = value;
    }
    delete result.customFields;
  }
  return result;
}

const initialState = {
  isActive: null,
  proseGeneratorActive: false,
  chapterEditorActive: false,
  selectedFeedbackCategory: null,
  hasUserSelectedFeedbackCategory: false,
  feedbackCategoryByProjectId: {},
  /** Agentic chat: increment + optional type list for next Feedback run (ChapterEditor consumes). */
  agenticChatFeedbackRunRequest: { id: 0, feedbackTypeNames: null, documentId: null },
  feedbackTriggerCount: 0, // Incremented to signal ChapterEditor to start analysis (7957-A)
  feedbackRequest: null,
  feedbackPending: false, // True when a trigger has been requested but not yet consumed by ChapterEditor
  /** Source for the currently pending feedback trigger (used for diagnostics/tracing). */
  feedbackTriggerSource: null,
  feedbackModel: "gemini-2.5-pro", // Feedback quality model — moved from ChapterEditor local state (7957-A)
  feedbackAbortController: null, // AbortController for feedback generation — lifted from ChapterEditor
  commentSidebarOpen: false, // Controls comment sidebar visibility (8210-A) — not persisted
  draftStickyBarHeight: 0, // Height of Draft's GeneratePanel when in sticky/fixed mode (8210-C)
  saved: true,
  seed: null,
  isStoryBibleLoaded: false, // True when story bible data (seed) has been loaded
  synopsis: null,
  collapsedSections: {
    seed: false,
    genre: false,
    style: false,
    synopsis: false,
    characters: false,
    worldbuilding: false,
    outline: false,
  },
  metadata: {
    genre: null,
    style: null,
    draftProseMode: null, // Project-scoped Draft prose mode selection
    writeProseMode: null, // Project-scoped Write prose mode selection
  },
  characters: null,
  outline: null,
  scenes: {},
  sceneId: null,
  options: [],
  sceneToWrite: null,
  orphanedChapter: null, // if a chapter is linked to an outline section that is removed from the outline
  isLoadingSceneToWrite: false,
  outlineObject: null,
  helpContents: null,
  helpPosition: { x: 0, y: 0 },
  recentInvocations: {
    synopsis: null,
    characters: null,
    outline: null,
    commands: {},
    sceneProse: {},
    lastInvocationType: null,
  },
  lastInvocation: null,
  isStreaming: false,
  isProseStreaming: false,
  outOfQuota: false,
  requesting: false,
  streamTimedOut: false,
  history: null,
  previousSceneLabel: "",
  isInView: null,
  sectionInView: null,
  onboardingIndex: null,
  chapterSelectorValue: null,
  // Refs - since refs are POJOs, and difficult to create here, we initialize as objects directly instead of createRef or useRef
  streamRequestRef: { current: null },
  isStreamingRef: { current: null },
  isProseStreamingRef: { current: null },
  abortStreamRef: { current: null },
  abortController: { current: new AbortController() },
  owner: null,
  storyEngineIdRef: { current: null },
  elementRefs: {
    top: { current: null },
    seed: { current: null },
    genre: { current: null },
    style: { current: null },
    synopsis: { current: null },
    characters: { current: null },
    worldbuilding: { current: null },
    outline: { current: null },
  },
  requestingRef: { current: false },
  PROSE_MODES: PROSE_MODES,
  proseMode: null,
  featuredStyles: null,
  documentScenes: {},
  /** documentId -> counter; incremented when agent/tool refreshes Draft scenes from server (forces TipTap remount). */
  draftScenesAgentRefreshNonce: {},
  scenesHistoryOpen: false,
  preflightResponse: null,

  // these are only for getting access to the data.  The data is edited elsewhere.
  charactersEntities: [],
  charactersEntitiesProjectId: null,
  worldbuildingEntries: [],
  worldbuildingEntriesProjectId: null,
  refreshingEntities: false,
  refreshingCharactersByProjectId: {},
  refreshingWorldbuildingByProjectId: {},
  charactersLoadFailuresByProjectId: {},
  worldbuildingLoadFailuresByProjectId: {},
  characterRefreshTrigger: 0,
  worldbuildingRefreshTrigger: 0,
  forceRenderSearchables: false, // When true, forces lazy-loaded sections to render for global search
  forceSaveRef: { current: null }, // Ref to forceSave function from usePersist

  // Agentic chat field diffs - maps entityId -> { oldFields, newFields }
  characterDiffs: {},
  worldbuildingDiffs: {},
  metaDiffs: {}, // { synopsis: true, genre: true, ... } - tracks which meta fields were updated

  // Track IDs of newly created entities (all fields should show as purple)
  pendingNewCharacterIds: [],
  pendingNewWorldbuildingIds: [],
};

export const useStoryBibleStore = create(
  persist(
    (set, get) => {
      const debouncedGetPOVAndTense = debounce(async (synopsis, projectId, setStorePOVTense) => {
        // Detects POV and Tense whenever the synopsis changes
        const project = await Project.get(projectId);
        const subscription = await Subscription.fetchFromUserId(project.data.userId);
        const { hasAccessToPOVAndTenseBeta } = subscription.getFeatureFlags();

        if (!hasAccessToPOVAndTenseBeta || !synopsis) {
          return;
        }

        const projectFlags = await Project.getFlags(projectId);

        // Only detect POV and tense for new projects (created within last 2 minutes)
        if (!projectFlags?.detect_pov_tense_from_synopsis) {
          return;
        }

        const genre = get().metadata?.genre;

        const povTense = await project.detectPOVTense(synopsis, project.data.userId, genre);

        // Update the Project store so that the UI updates to show the detected POV/Tense
        if (setStorePOVTense) {
          setStorePOVTense(povTense);
        }
      }, 500);

      return {
        ...initialState,

        // A distinct object identifies each mounted load, including A → B → A.
        acquireOwner: (projectId) => {
          const owner = { projectId };
          set({ owner, storyEngineIdRef: { current: null }, seed: null, synopsis: null, metadata: {}, scenes: {}, characters: null, outline: null, saved: true, isStoryBibleLoaded: false });
          return owner;
        },
        hydrateOwner: (owner, storyEngineId, fields) => {
          if (get().owner !== owner) return;
          set({ ...fields, storyEngineIdRef: { current: storyEngineId }, isStoryBibleLoaded: true });
        },
        releaseOwner: (owner) => {
          if (get().owner === owner) get().resetSoft();
        },
        isCurrentOwner: (owner) => Boolean(owner && get().owner === owner),

        setIsActive: (isActive) => set({ isActive }),

        setProseGeneratorActive: (proseGeneratorActive) => set({ proseGeneratorActive }),

        setChapterEditorActive: (chapterEditorActive) => set({ chapterEditorActive }),

        setSelectedFeedbackCategory: (selectedFeedbackCategory, projectId = null) =>
          set((state) => ({
            selectedFeedbackCategory,
            hasUserSelectedFeedbackCategory: true,
            ...(projectId
              ? {
                  feedbackCategoryByProjectId: {
                    ...(state.feedbackCategoryByProjectId || {}),
                    [projectId]: {
                      selectedFeedbackCategory,
                      hasUserSelectedFeedbackCategory: true,
                      updatedAt: Date.now(),
                    },
                  },
                }
              : {}),
          })),

        /**
         * Agentic chat: run manuscript Feedback, optionally on a resolved off-screen document.
         * @param {string[] | null | undefined} feedbackTypeNames omit or null: all enabled; []: all enabled; non-empty: exact type names
         * @param {{ fromPicker?: boolean, documentId?: string, feedbackRunId?: string }} [opts] fromPicker: user completed choose_feedback_types; suppress assistant tool overriding types briefly
         */
        requestManuscriptFeedbackFromAgent: (feedbackTypeNames, opts) => {
          const now = Date.now();
          const fromPicker = opts?.fromPicker === true;

          const targetDocumentId = typeof opts?.documentId === "string" && opts.documentId.trim()
            ? opts.documentId.trim()
            : null;
          const canonical = canonicalAgenticFeedbackRequest(feedbackTypeNames);
          const { normalized } = canonical;
          const sig = `${canonical.sig}|document:${targetDocumentId ?? "open"}`;
          if (!fromPicker && now < suppressAgenticFeedbackToolOverrideUntilMs) {
            return;
          }
          if (sig === agenticFeedbackRunDedupe.sig && now - agenticFeedbackRunDedupe.t < AGENTIC_FEEDBACK_RUN_DEDUPE_MS) {
            return;
          }
          agenticFeedbackRunDedupe = { sig, t: now };
          if (fromPicker) {
            suppressAgenticFeedbackToolOverrideUntilMs = now + AGENTIC_SUPPRESS_FEEDBACK_OVERRIDE_MS;
          }

          set((state) => ({
            agenticChatFeedbackRunRequest: {
              id: state.agenticChatFeedbackRunRequest.id + 1,
              feedbackTypeNames: normalized,
              documentId: targetDocumentId,
              feedbackRunId: opts?.feedbackRunId,
            },
          }));
        },

        /** Mark the current agentic chat feedback run request as consumed so remounts do not replay it. */
        consumeAgenticChatFeedbackRunRequest: (requestId) =>
          set((state) => {
            const currentId = state.agenticChatFeedbackRunRequest?.id;
            if (!requestId || currentId !== requestId) return {};
            return { agenticChatFeedbackRunRequest: { id: 0, feedbackTypeNames: null, documentId: null } };
          }),

        /**
         * Queue a feedback run.
         * @param {"button"|"agentic_chat"|"unknown"} source
         */
        triggerFeedback: (source = "button", request = null) => set((state) => ({
          feedbackTriggerCount: state.feedbackTriggerCount + 1,
          feedbackPending: true,
          feedbackTriggerSource: source,
          feedbackRequest: request,
          hasUserSelectedFeedbackCategory: true,
        })),

        clearFeedbackPending: () => set({ feedbackPending: false, feedbackTriggerSource: null, feedbackRequest: null }),

        setFeedbackModel: (feedbackModel) => set({ feedbackModel }),

        // Request ownership survives the document-keyed ChapterEditor remounting.
        setFeedbackAbortController: (controller) => {
          const previous = get().feedbackAbortController;
          set({ feedbackAbortController: controller });
          useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.FEEDBACK_RUNNING, !!controller);
          if (controller && previous && previous !== controller) previous.abort();
        },

        abortFeedback: () => {
          const controller = get().feedbackAbortController;
          if (controller) {
            controller.abort();
          }
          get().setFeedbackAbortController(null);
          set({ chapterEditorActive: false });
          useCommentStore.getState().setWaitingForFeedbackComments(false);
        },

        setCommentSidebarOpen: (commentSidebarOpen) => set({ commentSidebarOpen }),

        toggleCommentSidebar: () => set((state) => ({ commentSidebarOpen: !state.commentSidebarOpen })),

        setDraftStickyBarHeight: (draftStickyBarHeight) => set({ draftStickyBarHeight }),

        setSaved: (saved) => set({ saved }),

        setSeed: (seed) => set({ seed, isStoryBibleLoaded: seed !== null }),

        setCollapsedSections: (collapsedSections) => set({ collapsedSections }),

        toggleSectionCollapsed: (section) => {
          const current = get().collapsedSections;
          set({
            collapsedSections: {
              ...current,
              [section]: !current[section],
            },
          });
        },

        setSectionCollapsed: (section, collapsed) => {
          const current = get().collapsedSections;
          set({
            collapsedSections: {
              ...current,
              [section]: collapsed,
            },
          });
        },

        setSynopsis: (synopsis, projectId = undefined, isLoading = false, setStorePOVTense = null) => {
          if (projectId && get().owner?.projectId !== projectId) return;
          const prevState = get().synopsis;
          set({ synopsis });

          if (prevState && synopsis && prevState.trim() === synopsis.trim()) {
            return;
          }

          if (!isLoading && setStorePOVTense) {
            // Debounced so we don't detect tense on every keystroke
            debouncedGetPOVAndTense(synopsis, projectId, setStorePOVTense);
          }
        },

        setForceRenderSearchables: (forceRenderSearchables) => set({ forceRenderSearchables }),

        setForceSave: (forceSave) => {
          get().forceSaveRef.current = forceSave;
        },

        setMetadata: (fn) => set((state) => ({ metadata: produce(state.metadata, fn) })),

        setCharacters: (characters) => set({ characters }),

        setOutline: (outline) => (typeof outline === "function" ? set((state) => ({ outline: produce(state.outline, outline) })) : set({ outline })),

        setDocumentScenes: (documentId, entity) => set((state) => ({ documentScenes: { ...state.documentScenes, [documentId]: entity } })),

        bumpDraftScenesAgentRefresh: (documentId) =>
          set((state) => ({
            draftScenesAgentRefreshNonce: {
              ...state.draftScenesAgentRefreshNonce,
              [documentId]: (state.draftScenesAgentRefreshNonce[documentId] ?? 0) + 1,
            },
          })),

        getDocumentScenes: (documentId) => get().documentScenes[documentId],

        hasDocumentScenes: (documentId) => {
          const state = get();
          return !!state.documentScenes?.[documentId]?.value?.scenes?.content?.[0]?.content?.[0]?.content?.[0]?.text ?? null;
        },

        getDocumentSceneCount: (documentId) => {
          const state = get();
          return state.documentScenes?.[documentId]?.value?.scenes?.content?.[0]?.content?.length || 0;
        },

        getExtraInstructionsWordCount: (documentId) => {
          const state = get();
          return countWords(state.documentScenes?.[documentId]?.value?.extraInstructions || "");
        },

        setScenes: (scenes) => {
          if (typeof scenes === "function") {
            const pro = produce(get().scenes, scenes);
            if (Object.keys(pro).length < Object.keys(get().scenes) || Object.keys(pro).length === 0) {
              return;
            }
            set((state) => {
              return { scenes: produce(state.scenes, scenes) };
            });
          } else {
            console.log("setScenes literal ", scenes);
            if (Object.keys(scenes).length < Object.keys(get().scenes).length || Object.keys(scenes) === 0) {
              return;
            }
            set({ scenes });
          }
        },

        setSceneId: (sceneId) => set({ sceneId }),

        resetDocumentDependentState: () => {
          const { abortController } = get();
          abortController?.current?.abort();
          set({
            sceneId: null,
            sceneToWrite: null,
            orphanedChapter: null,
            isLoadingSceneToWrite: false,
            preflightResponse: null,
            abortController: { current: new AbortController() },
          });
        },

        setOptions: (options) => set({ options }),

        setSceneToWrite: (sceneToWrite) => set({ sceneToWrite }),

        setOrphanedChapter: (orphanedChapter) => set({ orphanedChapter }),

        setIsLoadingSceneToWrite: (isLoadingSceneToWrite) => set({ isLoadingSceneToWrite }),

        setOutlineObject: (outlineObject) => set({ outlineObject }),

        setHelpContents: (helpContents) => set({ helpContents }),

        setHelpPosition: (helpPosition) => set({ helpPosition }),

        setRecentInvocations: (recentInvocations) =>
          typeof recentInvocations === "function" ? set((state) => ({ recentInvocations: recentInvocations(state.recentInvocations) })) : set({ recentInvocations }),

        setLastInvocation: (lastInvocation) => set({ lastInvocation }),

        setIsStreaming: (isStreaming) => {
          get().isStreamingRef.current = isStreaming;
          set({ isStreaming });

          // Turn off the special case of prose streaming whenever general streaming stops
          // This prevents needing special prose stream handlers everywhere
          if (!isStreaming && get().isProseStreaming) {
            get().setIsProseStreaming(false);
          }
        },

        setIsProseStreaming: (isProseStreaming) => {
          get().isProseStreamingRef.current = isProseStreaming;
          set({ isProseStreaming });
        },

        setOutOfQuota: (outOfQuota) => set({ outOfQuota }),

        setRequesting: (requesting) => {
          get().requestingRef.current = requesting;
          set({ requesting });
        },

        setStreamTimedOut: (streamTimedOut) => set({ streamTimedOut }),

        setHistory: (history) => set({ history }),

        setPreviousSceneLabel: (previousSceneLabel) => set({ previousSceneLabel }),

        setIsInView: (isInView) => set({ isInView }),

        setSectionInView: (sectionInView) => set({ sectionInView }),

        setChapterSelectorValue: (chapterSelectorValue) => set({ chapterSelectorValue }),

        setStreamRequestRef: (streamRequestRef) => {
          get().streamRequestRef.current = streamRequestRef;
        },

        setFeaturedStyles: (featuredStyles) => set({ featuredStyles }),

        abortStream: () => {
          get().abortController.current.abort();
          get().abortController.current = new AbortController();
          get().setRequesting(false);
          get().setIsStreaming(false);
        },

        resetSoft: () => {
          const newState = { ...initialState, storyEngineIdRef: { current: null } };
          delete newState.proseGeneratorActive;
          set(newState);
        },

        resetHard: () => {
          set({ ...initialState, storyEngineIdRef: { current: null } });
        },

        isEmpty: () => {
          const state = get();
          const commands = !!getIn(state, ["scenes", "*", "commands"]).filter((v) => !!v).length;
          const seed = !!state.seed;
          const genre = !!state.metadata?.genre;
          const style = !!state.metadata?.style;
          const synopsis = !!state.synopsis;
          const characters = !!state.synopsis;
          const outline = !!state.outline;
          return !commands && !seed && !genre && !style && !synopsis && !characters && !outline;
        },

        // Return the section and scene indices for the highest linked scene
        getHighestLinkedScene: (project) => {
          const activeDocs = project.data.contents.contents.map((i) => i.id);
          const scenesRaw = get().scenes;
          const scenes = getIn(scenesRaw, [(i) => activeDocs.includes(i?.contents?.id)]);
          const numberedSections = getIn(scenes, [(i) => typeof i?.section === "number"], []);
          const highestSectionIndex = Math.max(...getIn(numberedSections, ["*", "section"], []));
          const highestSections = getIn(scenes, [(i) => i.section === highestSectionIndex]);
          const highestSceneIndex = Math.max(...getIn(highestSections, ["*", "scene"]));
          return { highestSectionIndex: highestSectionIndex === -Infinity ? null : highestSectionIndex, highestSceneIndex: highestSceneIndex === -Infinity ? null : highestSceneIndex };
        },

        createScene: (documentId, sectionIndex = null, sceneIndex = null) => {
          const id = makeId(6);
          get().setScenes((draft) => {
            draft[id] = {
              contents: {
                type: "document",
                id: documentId,
              },
              id: id,
              summary: "",
              commands: "",
              section: sectionIndex,
              scene: sceneIndex,
              createdAt: Date.now(),
            };
          });
          return id;
        },
        createStructureOutlineScene: (documentId, itemId) => {
          const id = makeId(6);
          get().setScenes((draft) => {
            draft[id] = {
              contents: {
                type: "document",
                id: documentId,
              },
              id: id,
              summary: "",
              commands: "",
              itemId: itemId,
              createdAt: Date.now(),
            };
          });
          get().triggerImmediateSave?.();
          return id;
        },
        triggerImmediateSave: () => {
          const forceSave = get().forceSaveRef?.current;
          if (typeof forceSave === "function") {
            forceSave();
          }
        },
        getSceneForDocument: (documentId) => {
          return Object.values(get().scenes).find((i) => i?.contents?.id === documentId);
        },

        clearLastInvocation: _.debounce((type = null, id = null) => {
          if (!get().setRecentInvocations) return;
          get().setRecentInvocations((prevState) => {
            const newState = { ...prevState };
            if (id && type) {
              newState[type][id] = null;
            } else if (type) {
              const initialValue = type === "commands" || type === "sceneProse" ? {} : null;
              newState[type] = initialValue;
            }
            return newState;
          });
        }, 2000),

        openCommandsHelp: (e) => {
          get().updateHelpPosition(e);
          get().setHelpContents(
            <div>
              <p>
                Beats are a set of step-by-step instructions for the AI or “junior writing partner” to follow when writing this Chapter. Make sure each beat has enough detail to turn into 100 - 200
                words.
                <br />
                <br />
                Bad Beat: 1. Todd welcomes Eli to the mansion.
                <br /> Good Beat: 1. Todd says hi to Eli at the entrance of the mansion. Describe the statues and art in the entrance. Then, Todd shows Eli to his room, pointing out the Tuscan
                architecture details and humble-bragging about the famous people who have visited before.
                <br />
                <br />
                You can add special instructions for the AI in [square brackets] for greater control. e.g. [Write Todd's dialogue in the form of a limerick]
              </p>
              <ul>
                This section is generated based on:
                <li>Braindump</li>
                <li>Genre</li>
                <li>Style</li>
                <li>Synopsis</li>
                <li>Characters</li>
                <li>Outline</li>
              </ul>
            </div>
          );
        },

        openProseHelp: (e) => {
          get().updateHelpPosition(e);
          get().setHelpContents(
            <div>
              <h3>How it works</h3>
              <p>The Prose section generates a first draft for you to edit into your manuscript</p>
              <ul>
                This section is generated based on:
                <li>Genre</li>
                <li>Style</li>
                <li>Characters</li>
                <li>This Chapter's Beats</li>
              </ul>
            </div>
          );
        },

        updateHelpPosition: (e) => {
          const xPos = e.clientX + 20;
          const yPos = e.clientY + 20;
          get().setHelpPosition({ x: xPos, y: yPos });
          setTimeout(() => {
            get().setHelpPosition({ x: xPos, y: yPos - 15 });
          }, 10);
        },

        closeHelp: () => {
          get().setHelpContents(null);
        },

        setProseModes: (proseModes) => set({ PROSE_MODES: proseModes }),

        setProseMode: (proseMode) => set({ proseMode }),

        setScenesHistoryOpen: (scenesHistoryOpen) => set({ scenesHistoryOpen }),

        setPreflightResponse: (preflightResponse) => set({ preflightResponse }),

        upsertCharacterEntity: (character, projectId = null) =>
          set((state) => {
            if (!character?.id) return {};
            // Explicit scope comes from the active stream's view, not the card's
            // originating book (series cards retain their original projectId).
            const sameProject = !projectId || state.charactersEntitiesProjectId === projectId;
            const current = sameProject && Array.isArray(state.charactersEntities) ? state.charactersEntities.filter((c) => c?.id) : [];
            const exists = current.some((c) => c.id === character.id);
            const updated = exists
              ? current.map((c) => (c.id === character.id ? character : c))
              : [...current, character];
            return {
              charactersEntities: updated,
              charactersEntitiesProjectId: projectId || state.charactersEntitiesProjectId || character.projectId,
              characterRefreshTrigger: state.characterRefreshTrigger + 1,
            };
          }),

        /** Sync global list from an in-memory character array (same shape as /api/characters/list). Avoids a second network fetch when local state already has the latest list. */
        syncCharactersEntitiesFromList: (characters, projectId = null) =>
          set((state) => {
            const list = Array.isArray(characters) ? characters : [];
            const withFloats = list.filter((c) => c?.id).map((c) => ({ ...c, displayOrder: parseFloat(c.displayOrder || 0) }));
            const deduped = [...new Map(withFloats.map((c) => [c.id, c])).values()];
            const sorted = deduped.sort((a, b) => a.displayOrder - b.displayOrder);
            const refreshingCharactersByProjectId = projectId
              ? setProjectFlag(state.refreshingCharactersByProjectId, projectId, false)
              : state.refreshingCharactersByProjectId;
            return {
              charactersEntities: sorted,
              charactersEntitiesProjectId: projectId || state.charactersEntitiesProjectId,
              refreshingEntities: hasProjectFlags(refreshingCharactersByProjectId) || hasProjectFlags(state.refreshingWorldbuildingByProjectId),
              refreshingCharactersByProjectId,
              charactersLoadFailuresByProjectId: projectId
                ? setProjectFlag(state.charactersLoadFailuresByProjectId, projectId, false)
                : state.charactersLoadFailuresByProjectId,
            };
          }),

        refreshCharacters: async (projectId, { force = false, shouldApply } = {}) => {
          if (!force && _inflightCharacters.has(projectId)) {
            return _inflightCharacters.get(projectId);
          }
          const refreshVersion = ++_entityRefreshSequence;
          _charactersRefreshVersions.set(projectId, refreshVersion);
          const promise = (async () => {
            try {
              set((state) => ({
                refreshingEntities: true,
                refreshingCharactersByProjectId: setProjectFlag(state.refreshingCharactersByProjectId, projectId, true),
                charactersLoadFailuresByProjectId: setProjectFlag(state.charactersLoadFailuresByProjectId, projectId, false),
              }));
              const response = await Auth.fetchWithIdToken(
                "/api/characters/list",
                { projectId },
                null,
                {
                  requestSource: "storyBibleStore.refreshCharacters",
                  responseReadFailureRetries: STORY_BIBLE_RESPONSE_READ_FAILURE_RETRIES,
                  responseReadFailureRetryDelayMs: STORY_BIBLE_RESPONSE_READ_FAILURE_RETRY_DELAY_MS,
                },
              );
              const list = Array.isArray(response?.characters) ? response.characters.filter((c) => c?.id) : [];
              const currentVersion = _charactersRefreshVersions.get(projectId);
              if (currentVersion !== refreshVersion) {
                reportStaleStoryBibleEntityRefresh("characters", projectId, refreshVersion, currentVersion, force);
                return;
              }
              set((state) => {
                const refreshingCharactersByProjectId = setProjectFlag(state.refreshingCharactersByProjectId, projectId, false);
                // Recovery can finish after navigation. Retire the request without
                // replacing the active project's list or notifying its sidebar.
                if (shouldApply && !shouldApply()) return {
                  refreshingCharactersByProjectId,
                  refreshingEntities: hasProjectFlags(refreshingCharactersByProjectId) || hasProjectFlags(state.refreshingWorldbuildingByProjectId),
                };
                return {
                  charactersEntities: list,
                  charactersEntitiesProjectId: projectId,
                  refreshingEntities: hasProjectFlags(refreshingCharactersByProjectId) || hasProjectFlags(state.refreshingWorldbuildingByProjectId),
                  refreshingCharactersByProjectId,
                  charactersLoadFailuresByProjectId: setProjectFlag(state.charactersLoadFailuresByProjectId, projectId, false),
                  characterRefreshTrigger: state.characterRefreshTrigger + 1,
                };
              });
            } catch (error) {
              const currentVersion = _charactersRefreshVersions.get(projectId);
              reportStoryBibleEntityRefreshFailure(
                error,
                "characters",
                projectId,
                refreshVersion,
                currentVersion,
                force,
              );
              if (currentVersion !== refreshVersion) return;
              set((state) => {
                const refreshingCharactersByProjectId = setProjectFlag(state.refreshingCharactersByProjectId, projectId, false);
                return {
                  refreshingEntities: hasProjectFlags(refreshingCharactersByProjectId) || hasProjectFlags(state.refreshingWorldbuildingByProjectId),
                  refreshingCharactersByProjectId,
                  charactersLoadFailuresByProjectId: setProjectFlag(state.charactersLoadFailuresByProjectId, projectId, true),
                };
              });
              throw error;
            }
          })();
          _inflightCharacters.set(projectId, promise);
          const cleanup = () => {
            if (_inflightCharacters.get(projectId) === promise) {
              _inflightCharacters.delete(projectId);
              _charactersRefreshVersions.delete(projectId);
            }
          };
          void promise.then(cleanup, cleanup);
          return promise;
        },

        upsertWorldbuildingEntity: (entry) =>
          set((state) => {
            if (!entry?.id) return {};
            const current = Array.isArray(state.worldbuildingEntries) ? state.worldbuildingEntries.filter((e) => e?.id) : [];
            const exists = current.some((e) => e.id === entry.id);
            const updated = exists
              ? current.map((e) => (e.id === entry.id ? entry : e))
              : [...current, entry];
            return { worldbuildingEntries: updated };
          }),

        /** Sync global list from an in-memory entries array (same shape as /api/worldbuildingEntries/list). */
        syncWorldbuildingEntriesFromList: (entries, projectId = null) =>
          set((state) => {
            const list = Array.isArray(entries) ? entries : [];
            const parsed = list.filter((e) => e?.id).map((e) => ({ ...e, displayOrder: parseFloat(e.displayOrder) }));
            const sorted = [...new Map(parsed.map((e) => [e.id, e])).values()].sort((a, b) => a.displayOrder - b.displayOrder);
            const refreshingWorldbuildingByProjectId = projectId
              ? setProjectFlag(state.refreshingWorldbuildingByProjectId, projectId, false)
              : state.refreshingWorldbuildingByProjectId;
            return {
              worldbuildingEntries: sorted,
              worldbuildingEntriesProjectId: projectId || state.worldbuildingEntriesProjectId,
              refreshingEntities: hasProjectFlags(state.refreshingCharactersByProjectId) || hasProjectFlags(refreshingWorldbuildingByProjectId),
              refreshingWorldbuildingByProjectId,
              worldbuildingLoadFailuresByProjectId: projectId
                ? setProjectFlag(state.worldbuildingLoadFailuresByProjectId, projectId, false)
                : state.worldbuildingLoadFailuresByProjectId,
            };
          }),

        refreshWorldbuildingEntries: async (projectId, source = "unspecified", { force = false, shouldApply } = {}) => {
          if (!force && _inflightWorldbuilding.has(projectId)) {
            return _inflightWorldbuilding.get(projectId);
          }
          const refreshVersion = ++_entityRefreshSequence;
          _worldbuildingRefreshVersions.set(projectId, refreshVersion);
          const promise = (async () => {
            try {
              set((state) => ({
                refreshingEntities: true,
                refreshingWorldbuildingByProjectId: setProjectFlag(state.refreshingWorldbuildingByProjectId, projectId, true),
                worldbuildingLoadFailuresByProjectId: setProjectFlag(state.worldbuildingLoadFailuresByProjectId, projectId, false),
              }));
              const res = await Auth.fetchWithIdToken(
                "/api/worldbuildingEntries/list",
                { projectId },
                null,
                {
                  requestSource: `storyBibleStore.refreshWorldbuildingEntries:${source}`,
                  responseReadFailureRetries: STORY_BIBLE_RESPONSE_READ_FAILURE_RETRIES,
                  responseReadFailureRetryDelayMs: STORY_BIBLE_RESPONSE_READ_FAILURE_RETRY_DELAY_MS,
                },
              );
              const list = Array.isArray(res?.entries) ? res.entries.filter((e) => e?.id) : [];
              const currentVersion = _worldbuildingRefreshVersions.get(projectId);
              if (currentVersion !== refreshVersion) {
                reportStaleStoryBibleEntityRefresh("worldbuilding", projectId, refreshVersion, currentVersion, force, source);
                return;
              }
              set((state) => {
                const refreshingWorldbuildingByProjectId = setProjectFlag(state.refreshingWorldbuildingByProjectId, projectId, false);
                if (shouldApply && !shouldApply()) return {
                  refreshingWorldbuildingByProjectId,
                  refreshingEntities: hasProjectFlags(state.refreshingCharactersByProjectId) || hasProjectFlags(refreshingWorldbuildingByProjectId),
                };
                return {
                  worldbuildingEntries: list,
                  worldbuildingEntriesProjectId: projectId,
                  refreshingEntities: hasProjectFlags(state.refreshingCharactersByProjectId) || hasProjectFlags(refreshingWorldbuildingByProjectId),
                  refreshingWorldbuildingByProjectId,
                  worldbuildingLoadFailuresByProjectId: setProjectFlag(state.worldbuildingLoadFailuresByProjectId, projectId, false),
                  worldbuildingRefreshTrigger: state.worldbuildingRefreshTrigger + 1,
                };
              });
            } catch (error) {
              const currentVersion = _worldbuildingRefreshVersions.get(projectId);
              reportStoryBibleEntityRefreshFailure(
                error,
                "worldbuilding",
                projectId,
                refreshVersion,
                currentVersion,
                force,
                source,
              );
              if (currentVersion !== refreshVersion) return;
              set((state) => {
                const refreshingWorldbuildingByProjectId = setProjectFlag(state.refreshingWorldbuildingByProjectId, projectId, false);
                return {
                  refreshingEntities: hasProjectFlags(state.refreshingCharactersByProjectId) || hasProjectFlags(refreshingWorldbuildingByProjectId),
                  refreshingWorldbuildingByProjectId,
                  worldbuildingLoadFailuresByProjectId: setProjectFlag(state.worldbuildingLoadFailuresByProjectId, projectId, true),
                };
              });
              throw error;
            }
          })();
          _inflightWorldbuilding.set(projectId, promise);
          const cleanup = () => {
            if (_inflightWorldbuilding.get(projectId) === promise) {
              _inflightWorldbuilding.delete(projectId);
              _worldbuildingRefreshVersions.delete(projectId);
            }
          };
          void promise.then(cleanup, cleanup);
          return promise;
        },

        setCharacterDiff: (characterId, oldFields, newFields) => {
          const flatOld = flattenEntityFields(oldFields);
          const flatNew = flattenEntityFields(newFields);
          set((state) => {
            const existingDiff = state.characterDiffs[characterId];
            return {
              characterDiffs: {
                ...state.characterDiffs,
                [characterId]: {
                  oldFields: existingDiff
                    ? { ...flatOld, ...existingDiff.oldFields }
                    : flatOld,
                  newFields: existingDiff
                    ? { ...existingDiff.newFields, ...flatNew }
                    : flatNew,
                },
              },
            };
          });
        },

        clearCharacterDiff: (characterId) => {
          set((state) => {
            const newDiffs = { ...state.characterDiffs };
            delete newDiffs[characterId];
            return { characterDiffs: newDiffs };
          });
        },

        clearCharacterFieldDiff: (characterId, fieldKey) => {
          set((state) => {
            const diff = state.characterDiffs[characterId];
            if (!diff) return state;

            const newOldFields = { ...diff.oldFields };
            const newNewFields = { ...diff.newFields };
            delete newOldFields[fieldKey];
            delete newNewFields[fieldKey];

            // If no fields left, remove the entire character from diffs
            if (Object.keys(newOldFields).length === 0 && Object.keys(newNewFields).length === 0) {
              const newDiffs = { ...state.characterDiffs };
              delete newDiffs[characterId];
              return { characterDiffs: newDiffs };
            }

            return {
              characterDiffs: {
                ...state.characterDiffs,
                [characterId]: { oldFields: newOldFields, newFields: newNewFields },
              },
            };
          });
        },

        clearAllCharacterDiffs: () => {
          set({ characterDiffs: {} });
        },

        setWorldbuildingDiff: (entryId, oldFields, newFields) => {
          const flatOld = flattenEntityFields(oldFields);
          const flatNew = flattenEntityFields(newFields);
          set((state) => {
            const existingDiff = state.worldbuildingDiffs[entryId];
            return {
              worldbuildingDiffs: {
                ...state.worldbuildingDiffs,
                [entryId]: {
                  oldFields: existingDiff
                    ? { ...flatOld, ...existingDiff.oldFields }
                    : flatOld,
                  // Merge successive agent updates (multiple worldbuilding SSE chunks).
                  newFields: existingDiff ? { ...existingDiff.newFields, ...flatNew } : flatNew,
                },
              },
            };
          });
        },

        clearWorldbuildingDiff: (entryId) => {
          set((state) => {
            const newDiffs = { ...state.worldbuildingDiffs };
            delete newDiffs[entryId];
            return { worldbuildingDiffs: newDiffs };
          });
        },

        clearWorldbuildingFieldDiff: (entryId, fieldKey) => {
          set((state) => {
            const diff = state.worldbuildingDiffs[entryId];
            if (!diff) return state;

            const newOldFields = { ...diff.oldFields };
            const newNewFields = { ...diff.newFields };
            delete newOldFields[fieldKey];
            delete newNewFields[fieldKey];

            // If no fields left, remove the entire entry from diffs
            if (Object.keys(newOldFields).length === 0 && Object.keys(newNewFields).length === 0) {
              const newDiffs = { ...state.worldbuildingDiffs };
              delete newDiffs[entryId];
              return { worldbuildingDiffs: newDiffs };
            }

            return {
              worldbuildingDiffs: {
                ...state.worldbuildingDiffs,
                [entryId]: { oldFields: newOldFields, newFields: newNewFields },
              },
            };
          });
        },

        clearAllWorldbuildingDiffs: () => {
          set({ worldbuildingDiffs: {} });
        },

        clearAllEntityDiffs: () => {
          set({
            characterDiffs: {},
            worldbuildingDiffs: {},
            metaDiffs: {},
            pendingNewCharacterIds: [],
            pendingNewWorldbuildingIds: [],
          });
        },

        setMetaDiff: (fieldKey) => {
          set((state) => ({
            metaDiffs: {
              ...state.metaDiffs,
              [fieldKey]: true,
            },
          }));
        },

        clearMetaDiff: (fieldKey) => {
          set((state) => {
            const newDiffs = { ...state.metaDiffs };
            delete newDiffs[fieldKey];
            return { metaDiffs: newDiffs };
          });
        },

        clearAllMetaDiffs: () => {
          set({ metaDiffs: {} });
        },

        // Pending new entity tracking - for new entities, ALL fields should be purple
        addPendingNewCharacter: (characterId) => {
          set((state) => {
            const alreadyExists = state.pendingNewCharacterIds.includes(characterId);
            return {
              pendingNewCharacterIds: alreadyExists
                ? state.pendingNewCharacterIds
                : [...state.pendingNewCharacterIds, characterId],
            };
          });
        },

        removePendingNewCharacter: (characterId) => {
          set((state) => ({
            pendingNewCharacterIds: state.pendingNewCharacterIds.filter((id) => id !== characterId),
          }));
        },

        addPendingNewWorldbuilding: (entryId) => {
          set((state) => ({
            pendingNewWorldbuildingIds: state.pendingNewWorldbuildingIds.includes(entryId)
              ? state.pendingNewWorldbuildingIds
              : [...state.pendingNewWorldbuildingIds, entryId],
          }));
        },

        removePendingNewWorldbuilding: (entryId) => {
          set((state) => ({
            pendingNewWorldbuildingIds: state.pendingNewWorldbuildingIds.filter((id) => id !== entryId),
          }));
        },

        clearAllPendingNewEntities: () => {
          set({ pendingNewCharacterIds: [], pendingNewWorldbuildingIds: [] });
        },
      };
    },
    {
      name: "story-bible",
      storage: createJSONStorage(() => createSafeStorage(typeof window !== "undefined" ? window.localStorage : null)),
      partialize: (state) => ({
        proseGeneratorActive: state.proseGeneratorActive,
        selectedFeedbackCategory: state.selectedFeedbackCategory,
        hasUserSelectedFeedbackCategory: state.hasUserSelectedFeedbackCategory,
      }),
    }
  )
);

export const withStoryBibleStore = (BaseComponent) => {
  const MemoizedBaseComponent = React.memo(BaseComponent, (prev, next) => {
    return _.isEqual(prev, next);
  });

  return (props) => {
    const selector = (state) => {
      return {
        isActive: state.isActive,
        characters: state.characters,
        clearLastInvocation: state.clearLastInvocation,
        isProseStreamingRef: state.isProseStreamingRef,
        isProseStreaming: state.isProseStreaming,
        setRecentInvocations: state.setRecentInvocations,
        recentInvocations: state.recentInvocations,
        scenes: state.scenes,
        sceneId: state.sceneId,
        proseGeneratorActive: state.proseGeneratorActive,
      };
    };

    const selectedState = useStoryBibleStore(selector);

    return (
      <MemoizedBaseComponent
        {...props}
        storyBibleStore={selectedState}
      />
    );
  };
};
